import { useAuth } from '@/components/auth/authProvider';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import FormSection from '@/components/Forms/FormSection';
import { FlagBox } from '@/components/QueueFlags/FlagBox';
import usePermissions from '@/hooks/usePermissions';
import { useGetApprovedVendorsByTypeQuery } from '@/services/approvedVendors';
import { Box, Button, FormikSelectInputNative, FormikTextInput } from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  RequirementFlagSettingsType,
  UserPermissions,
  VendorEquipmentTypes,
  WaterHeaterEquipmentItem,
  WaterHeaterEquipmentTypeLabels,
} from 'types';

interface EquipmentSectionProps {
  systemIndex: number;
  isPackageEditable?: boolean;
}

const getLinkForManufacturer = (manufacturer: string): string => {
  // Normalize the manufacturer name for comparison
  const normalizedManufacturer = manufacturer.toLowerCase().trim();

  // Map manufacturer names to warranty registration URLs
  const warrantyLinks: Record<string, string> = {
    rheem: 'https://rheem.registermyunit.com/v3/brand.html?brand=rheem',
    'a.o. smith': 'https://www.hotwater.com/support/warranty/',
    'bradford white': 'https://www.bradfordwhite.com/warranty-registration',
    rinnai: 'https://www.rinnai.us/warranty-registration',
    bosch: 'https://bosch-us-home.thernovo.com/home',
    navien: 'https://www.navieninc.com/support/warranty',
    ecosmart: 'https://www.ecosmartus.com/warranty-registration',
    'ge appliances': 'https://www.geappliances.com/ge/register',
    'american standard': 'https://www.americanstandardair.com/support/warranty-registration',
    'american water heaters': 'https://www.americanwaterheater.com/support/warranty/',
    whirlpool: 'https://www.whirlpool.com/register.html',
    kenmore: 'https://www.kenmore.com/warranty-information/',
  };

  return warrantyLinks[normalizedManufacturer] || '';
};

const dataKey = 'equipment' as const;
const label = 'Equipment Details' as const;

export const InstallPackageEquipmentSection = ({ systemIndex, isPackageEditable = true }: EquipmentSectionProps) => {
  const permissions = usePermissions();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );
  const { values } = useFormikContext<any>();
  const { id: accountId } = useParams<{ id: string }>();

  const { data: approvedVendors, isLoading: isLoadingVendors } = useGetApprovedVendorsByTypeQuery(
    { equipmentType: VendorEquipmentTypes.waterHeater, accountId },
    { skip: !accountId },
  );

  // Extract unique manufacturers from approved vendors, marking non-AVL manufacturers with "*"
  const manufacturerOptions = useMemo(() => {
    const vendors = approvedVendors?.data;
    const avlManufacturers = new Set<string>(
      vendors && Array.isArray(vendors) ? vendors.map((vendor: any) => vendor.manufacturer) : [],
    );

    const optionsMap = new Map<string, { label: string; value: string }>();

    for (const manufacturer of avlManufacturers) {
      optionsMap.set(manufacturer, { label: manufacturer, value: manufacturer });
    }

    const equipmentItems: WaterHeaterEquipmentItem[] = values?.systems?.[systemIndex]?.[dataKey]?.items || [];
    for (const item of equipmentItems) {
      if (item.manufacturer && !avlManufacturers.has(item.manufacturer) && !optionsMap.has(item.manufacturer)) {
        optionsMap.set(item.manufacturer, { label: `${item.manufacturer} *`, value: item.manufacturer });
      }
    }

    return [...optionsMap.values()].toSorted((a, b) => a.label.localeCompare(b.label));
  }, [approvedVendors, values?.systems, systemIndex]);

  return (
    <FormSection
      title={label}
      description="Enter the equipment details for the installed water heater system."
      baseStyle={{
        borderWidth: '0',
      }}
    >
      <FieldArray
        name={`systems.${systemIndex}[${dataKey}].items`}
        render={() => {
          return (
            <Box gap="lg">
              {values?.systems[systemIndex]?.[dataKey]?.items?.map?.(
                (
                  equipmentItem: WaterHeaterEquipmentItem & {
                    isSerialNumberRequired?: boolean;
                  },
                  index: number,
                ) => {
                  return (
                    <Box
                      key={`system-${systemIndex}-${dataKey}-${equipmentItem.type}-${index}`}
                      childGap="lg"
                      padding="lg"
                      background="secondary"
                      radius="md"
                    >
                      <Box fontSize="md" fontWeight="medium">
                        {WaterHeaterEquipmentTypeLabels[equipmentItem.type] || `Equipment ${index + 1}`}
                      </Box>

                      <Box
                        direction={{ base: 'column', tablet: 'row' }}
                        gap="lg"
                        alignItems={{ tablet: 'flex-start' }}
                      >
                        <Box flex="auto" minWidth="0">
                          <Field
                            label="Manufacturer"
                            name={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                            id={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                            component={FormikSelectInputNative}
                            options={[
                              { label: isLoadingVendors ? 'Loading...' : 'Select Manufacturer', value: '' },
                              ...manufacturerOptions,
                            ]}
                            isDisabled={userOnlyHasReaderPermission || isLoadingVendors || !isPackageEditable}
                            isRequired
                          />
                        </Box>

                        <Box flex="auto" minWidth="0">
                          <Field
                            label="Make/Model"
                            name={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                            id={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                            component={FormikTextInput}
                            placeholder="Enter make/model"
                            isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                            isRequired
                          />
                        </Box>

                        <Box flex="auto" minWidth="0">
                          <Field
                            label="Serial Number"
                            name={`systems.${systemIndex}[${dataKey}].items.${index}.serialNumbers.0`}
                            id={`systems.${systemIndex}[${dataKey}].items.${index}.serialNumbers.0`}
                            component={FormikTextInput}
                            placeholder="Enter serial number"
                            isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                            isRequired
                          />
                        </Box>
                      </Box>

                      {isFinCoUser &&
                        equipmentItem.manufacturer &&
                        getLinkForManufacturer(equipmentItem.manufacturer) && (
                          <Box>
                            <Link target="_blank" to={getLinkForManufacturer(equipmentItem.manufacturer)}>
                              <Button size="sm" variant="secondary">
                                Warranty Registration
                              </Button>
                            </Link>
                          </Box>
                        )}
                    </Box>
                  );
                },
              )}

              <FlagBox
                baseCategory="equipment"
                dataKey={`Installed ${label} Issue ${systemIndex + 1}`}
                flagType={RequirementFlagSettingsType.equipmentWaterHeater}
              />
            </Box>
          );
        }}
      />
    </FormSection>
  );
};
